import * as api from "./client";
import type {
  CmsPage,
  CmsPagePreviewLink,
  CmsBlock,
  CmsMenu,
  CmsMenuTree,
  CmsPageListResponse,
  CmsPageSingleResponse,
  CmsBlockListResponse,
  CmsBlockSingleResponse,
  CmsMenuListResponse,
  CmsMenuSingleResponse,
  CmsMenuTreeResponse,
  CreateCmsPageRequest,
  UpdateCmsPageRequest,
  DuplicateCmsPageRequest,
  CreateCmsBlockRequest,
  UpdateCmsBlockRequest,
  ReorderBlocksRequest,
  CreateCmsMenuRequest,
  UpdateCmsMenuRequest,
  ImportResult,
  PageFieldsEntry,
  PageFieldsListResponse,
  PageFieldsImportResult,
  CmsPagesImportFile,
  CmsPagesImportPreview,
  CmsPagesImportRequest,
  CmsPagesImportResult,
} from "../types/cms";
import type { PageSection } from "../pages/CMS/pageSection";
import { type DownloadedFile, readDownload } from "./fileDownload";

export type CmsPagesFilters = {
  slug?: string;
  section?: PageSection;
  search?: string;
  type?: string;
  status?: string;
  limit?: number;
  offset?: number;
};

export type PageFieldsFilters = {
  urlPath?: string;
  publication?: string;
  bpa?: string;
  limit?: number;
  offset?: number;
};

export type CmsMenusFilters = {
  parentId?: string | null;
  limit?: number;
  offset?: number;
};

export type CmsPagesExportScope = "page" | "sidebar";

export const CMS_IMAGE_MAX_SIZE_BYTES = 20 * 1024 * 1024;
export const CMS_IMAGE_SIZE_ERROR = "Choose an image no larger than 20 MiB.";

export const cms = {
  uploadImage: async (file: File): Promise<{ hash: string; filename: string; url: string }> => {
    const body = new FormData();
    body.append("file", file, file.name);
    const response = await api.fetchRaw("/v1/content/images", { method: "POST", body });
    if (!response.ok) {
      if (response.status === 413) throw new Error(CMS_IMAGE_SIZE_ERROR);
      let message = `Failed to upload image: HTTP ${response.status}`;
      try {
        const result = await response.json();
        if (typeof result.error === "string") message = result.error;
      } catch (cause) {
        throw new Error(message, { cause });
      }
      throw new Error(message);
    }
    return response.json();
  },
  getPages: async (filters?: CmsPagesFilters): Promise<{ pages: CmsPage[]; total: number }> => {
    const params = new URLSearchParams();
    if (filters?.slug) {
      params.append("slug", filters.slug);
    }
    if (filters?.section) params.append("section", filters.section);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.type) params.append("type", filters.type);
    if (filters?.status) params.append("status", filters.status);
    if (filters?.limit !== undefined) params.append("limit", filters.limit.toString());
    if (filters?.offset !== undefined) params.append("offset", filters.offset.toString());

    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await api.get<CmsPageListResponse>(`/v1/content/pages${query}`);
    return { pages: response.pages, total: response.pagination.total };
  },

  getPageById: async (id: string): Promise<CmsPage> => {
    const response = await api.get<CmsPageSingleResponse>(`/v1/content/pages/${id}`);
    return response.page;
  },

  createPage: async (data: CreateCmsPageRequest): Promise<CmsPage> => {
    const response = await api.post<CmsPageSingleResponse>("/v1/content/pages", data);
    return response.page;
  },

  updatePage: async (id: string, data: UpdateCmsPageRequest): Promise<CmsPage> => {
    const response = await api.put<CmsPageSingleResponse>(`/v1/content/pages/${id}`, data);
    return response.page;
  },

  duplicatePage: async (id: string, data: DuplicateCmsPageRequest): Promise<CmsPage> => {
    const response = await api.post<CmsPageSingleResponse>(
      `/v1/content/pages/${id}/duplicate`,
      data,
    );
    return response.page;
  },

  createPagePreviewLink: async (id: string): Promise<CmsPagePreviewLink> => {
    return api.post<CmsPagePreviewLink>(`/v1/content/pages/${id}/preview-link`);
  },

  deletePage: async (id: string): Promise<void> => {
    await api.del(`/v1/content/pages/${id}`);
  },

  exportPages: async (
    pageId: string,
    scope: CmsPagesExportScope,
    section?: PageSection,
  ): Promise<DownloadedFile> => {
    const params = new URLSearchParams({ pageId, scope, ...(section ? { section } : {}) });
    const response = await api.fetchRaw(`/v1/content/export/pages?${params.toString()}`);
    return readDownload(response, "cms-pages-export.json");
  },

  previewPagesImport: async (file: CmsPagesImportFile): Promise<CmsPagesImportPreview> => {
    return api.post<CmsPagesImportPreview>("/v1/content/import/pages/preview", file);
  },

  importPages: async (request: CmsPagesImportRequest): Promise<CmsPagesImportResult> => {
    return api.post<CmsPagesImportResult>("/v1/content/import/pages", request);
  },

  getBlocksByPageId: async (pageId: string): Promise<CmsBlock[]> => {
    const response = await api.get<CmsBlockListResponse>(`/v1/content/pages/${pageId}/blocks`);
    return response.blocks;
  },

  createBlock: async (data: CreateCmsBlockRequest): Promise<CmsBlock> => {
    const response = await api.post<CmsBlockSingleResponse>("/v1/content/blocks", data);
    return response.block;
  },

  updateBlock: async (id: string, data: UpdateCmsBlockRequest): Promise<CmsBlock> => {
    const response = await api.put<CmsBlockSingleResponse>(`/v1/content/blocks/${id}`, data);
    return response.block;
  },

  deleteBlock: async (id: string): Promise<void> => {
    await api.del(`/v1/content/blocks/${id}`);
  },

  reorderBlocks: async (
    pageId: string,
    blocks: Array<{ id: string; sortOrder: number }>,
  ): Promise<void> => {
    await api.put<void>("/v1/content/blocks/reorder", {
      pageId,
      blocks,
    } satisfies ReorderBlocksRequest);
  },

  getMenus: async (filters?: CmsMenusFilters): Promise<{ menus: CmsMenu[]; total: number }> => {
    const params = new URLSearchParams();
    if (filters?.parentId !== undefined) {
      params.append("parentId", filters.parentId === null ? "null" : filters.parentId);
    }
    if (filters?.limit !== undefined) params.append("limit", filters.limit.toString());
    if (filters?.offset !== undefined) params.append("offset", filters.offset.toString());

    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await api.get<CmsMenuListResponse>(`/v1/content/menus${query}`);
    return { menus: response.menus, total: response.pagination.total };
  },

  getMenuById: async (id: string): Promise<CmsMenu> => {
    const response = await api.get<CmsMenuSingleResponse>(`/v1/content/menus/${id}`);
    return response.menu;
  },

  getMenuTree: async (id: string, depth?: number): Promise<CmsMenuTree> => {
    const depthParam = depth !== undefined ? `?depth=${depth}` : "?depth=2";
    const response = await api.get<CmsMenuTreeResponse>(
      `/v1/content/menus/${id}/tree${depthParam}`,
    );
    return response.menu;
  },

  createMenu: async (data: CreateCmsMenuRequest): Promise<CmsMenu> => {
    const response = await api.post<CmsMenuSingleResponse>("/v1/content/menus", data);
    return response.menu;
  },

  updateMenu: async (id: string, data: UpdateCmsMenuRequest): Promise<CmsMenu> => {
    const response = await api.put<CmsMenuSingleResponse>(`/v1/content/menus/${id}`, data);
    return response.menu;
  },

  deleteMenu: async (id: string): Promise<void> => {
    await api.del(`/v1/content/menus/${id}`);
  },

  importLegacy: async (restApiKey: string): Promise<ImportResult> => {
    return api.post<ImportResult>("/v1/content/import/legacy", { restApiKey });
  },

  getPageFields: async (
    filters?: PageFieldsFilters,
  ): Promise<{ entries: PageFieldsEntry[]; total: number }> => {
    const params = new URLSearchParams();
    if (filters?.urlPath) {
      params.append("urlPath", filters.urlPath);
    }
    if (filters?.publication) {
      params.append("publication", filters.publication);
    }
    if (filters?.bpa) {
      params.append("bpa", filters.bpa);
    }
    if (filters?.limit !== undefined) {
      params.append("limit", filters.limit.toString());
    }
    if (filters?.offset !== undefined) {
      params.append("offset", filters.offset.toString());
    }

    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await api.get<PageFieldsListResponse>(`/v1/content/page-fields${query}`);
    return { entries: response.entries, total: response.pagination.total };
  },

  pageFieldsImport: async (file: File): Promise<PageFieldsImportResult> => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await api.fetchRaw("/v1/content/page-fields/import", {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      let message = `HTTP ${response.status}`;
      let code: string | undefined;
      try {
        const errorData = await response.json();
        if (errorData.error && typeof errorData.error === "string") {
          message = errorData.error;
        }
        if (errorData.code && typeof errorData.code === "string") {
          code = errorData.code;
        }
      } catch {
        // If JSON parsing fails, use the default error message
      }

      const error = new Error(message) as Error & { code?: string; status?: number };
      if (code) {
        error.code = code;
      }
      error.status = response.status;
      throw error;
    }

    return response.json() as Promise<PageFieldsImportResult>;
  },
};
